import { useCallback, useMemo, useState, useTransition } from "react";
import { useTypedSearchParams } from "react-router-typesafe-routes";

import type { ViewState } from "~/components/timeline";
import { useCompoundEntityVersions } from "~/features/compoundEntities/hooks";
import type { CompoundSearchCondition, CompoundView } from "~/features/compoundEntities/types";
import { v2Route } from "~/v2/routes";
import { CompoundEntitiesPresenter } from "./compoundEntities.presenter";

const DEFAULT_PAGE_SIZE = 10;
/** Default canvas width used to convert center-year ↔ offsetX before the timeline measures itself. */
const TIMELINE_CANVAS_WIDTH = 800;

export function CompoundEntitiesContainer() {
  const [params, setParams] = useTypedSearchParams(v2Route.compoundEntities);
  const [isPending, startTransition] = useTransition();
  const { versions } = useCompoundEntityVersions();

  // Search conditions live in local state only (not URL), matching v1 / the composers feature.
  const [searchConditions, setSearchConditions] = useState<CompoundSearchCondition[]>([]);

  const view: CompoundView = params.view ?? "table";
  const pageIndex = params.page ?? 0;
  const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
  const gdtfFrom = params.gdtfFrom;
  const gdtfTo = params.gdtfTo;

  // Effective version: the explicit URL selection, else the latest version.
  const effectiveVersion = params.version ?? versions[0]?.version;

  const onPageReset = useCallback(() => setParams((prev) => ({ ...prev, page: undefined })), [setParams]);

  const onConditionsChange = useCallback((next: CompoundSearchCondition[]) => setSearchConditions(next), []);

  const onGdtfCommit = useCallback(
    (from: string | undefined, to: string | undefined) =>
      startTransition(() => setParams((prev) => ({ ...prev, gdtfFrom: from, gdtfTo: to, page: undefined }))),
    [setParams],
  );

  const onVersionChange = useCallback(
    (next: string) => setParams((prev) => ({ ...prev, version: next || undefined, page: undefined })),
    [setParams],
  );

  const onViewChange = useCallback(
    (next: CompoundView) => setParams((prev) => ({ ...prev, view: next === "table" ? undefined : next })),
    [setParams],
  );

  const onPageIndexChange = useCallback(
    (next: number) => startTransition(() => setParams((prev) => ({ ...prev, page: next === 0 ? undefined : next }))),
    [setParams],
  );

  const onPageSizeChange = useCallback(
    (next: number) =>
      startTransition(() =>
        setParams((prev) => ({
          ...prev,
          pageSize: next === DEFAULT_PAGE_SIZE ? undefined : next,
          page: undefined,
        })),
      ),
    [setParams],
  );

  const selectedCompoundEntity = params.selectedCompoundEntity ?? null;
  const onSelect = useCallback(
    (id: string) => setParams((prev) => ({ ...prev, selectedCompoundEntity: id })),
    [setParams],
  );
  const onCloseSelected = useCallback(
    () => setParams((prev) => ({ ...prev, selectedCompoundEntity: undefined })),
    [setParams],
  );

  // ── Timeline view state ↔ URL (read once on mount; pan/zoom replaces history) ──
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialTimelineViewState = useMemo<ViewState | undefined>(() => {
    const { tlCenterYear, tlScale } = params;
    if (tlCenterYear == null || tlScale == null) return undefined;
    return { offsetX: TIMELINE_CANVAS_WIDTH / 2 - tlCenterYear * tlScale, offsetY: 0, scale: tlScale };
  }, []);
  const onTimelineViewStateChange = useCallback(
    (centerYear: number, scale: number) =>
      setParams((prev) => ({ ...prev, tlCenterYear: centerYear, tlScale: scale }), { replace: true }),
    [setParams],
  );

  return (
    <CompoundEntitiesPresenter
      view={view}
      onViewChange={onViewChange}
      searchConditions={searchConditions}
      onConditionsChange={onConditionsChange}
      onPageReset={onPageReset}
      gdtfFrom={gdtfFrom}
      gdtfTo={gdtfTo}
      onGdtfCommit={onGdtfCommit}
      versions={versions}
      selectedVersion={effectiveVersion}
      onVersionChange={onVersionChange}
      pageIndex={pageIndex}
      onPageIndexChange={onPageIndexChange}
      pageSize={pageSize}
      onPageSizeChange={onPageSizeChange}
      isPending={isPending}
      onSelect={onSelect}
      selectedCompoundEntity={selectedCompoundEntity}
      onCloseSelected={onCloseSelected}
      initialTimelineViewState={initialTimelineViewState}
      onTimelineViewStateChange={onTimelineViewStateChange}
    />
  );
}
